import React, { useRef, useEffect } from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import Typed from 'typed.js';
import gsap from 'gsap';
import ParticleLogo from './ParticleLogo';

const HeroSection = ({ heroRef }) => {
    const typedElement = useRef(null);

    useEffect(() => {
        const typed = new Typed(typedElement.current, {
            strings: [
                'Mobile Applications',
                'Web Development',
                'Cloud Solutions',
                'AI & Machine Learning',
                'Digital Transformation',
                'E-Commerce Platforms',
                'Custom Software',
                'API Development',
                'DevOps Solutions'
            ],
            typeSpeed: 100,
            backSpeed: 60,
            backDelay: 3000,
            startDelay: 500,
            loop: true,
            showCursor: true,
            cursorChar: '|',
            smartBackspace: true,
        });

        const ctx = gsap.context(() => {
            gsap.from('.hero-content-anim', {
                y: 30,
                opacity: 0,
                duration: 1.2,
                ease: 'power4.out',
            });
        }, heroRef);

        return () => {
            typed.destroy();
            ctx.revert();
        };
    }, [heroRef]);

    return (
        <section ref={heroRef} className="relative min-h-[90vh] flex items-center pt-24 pb-12 overflow-hidden bg-white">
            {/* Particle Earth Background */}
            <div className="absolute inset-0 z-0">
                <ParticleLogo />
            </div>

            {/* Hero Content */}
            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="max-w-4xl hero-content-anim text-center md:text-left">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 bg-blue-50 border border-blue-100 rounded-full backdrop-blur-xl"
                    >
                        <span className="flex h-1.5 w-1.5 rounded-full bg-blue-600 animate-pulse" />
                        <span className="text-[9px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-blue-600">
                            Innovation meets Excellence
                        </span>
                    </motion.div>

                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-black leading-[1.1] mb-6 tracking-tight">
                        <span className="block text-slate-900 mb-2">Architecting the</span>
                        <div className="text-gradient pb-1">
                            <span ref={typedElement}>Future of Tech</span>
                        </div>
                    </h1>

                    <p className="text-sm md:text-xl text-slate-600 mb-10 max-w-2xl mx-auto md:mx-0 leading-relaxed font-medium">
                        We build high-performance digital solutions that power the world's most innovative companies. From concept to deployment, we are your technology partners.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
                        <button className="w-full sm:w-auto px-8 py-4 bg-primary hover:bg-primary-dark text-white font-black uppercase tracking-widest text-xs rounded-full transition-all flex items-center justify-center gap-2.5 group shadow-xl shadow-blue-500/20 active:scale-95">
                            Get Started <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button className="w-full sm:w-auto px-8 py-4 bg-slate-50 hover:bg-slate-100 text-slate-900 font-black uppercase tracking-widest text-xs rounded-full border border-slate-200 transition-all flex items-center justify-center gap-2.5 group active:scale-95">
                            Our Works <Play size={16} fill="currentColor" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Scroll Indicator - Hidden on mobile to prevent overlap */}
            <div className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-2 opacity-40 z-10 pointer-events-none">
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Scroll to explore</span>
                <div className="w-[1px] h-12 bg-gradient-to-b from-blue-500 to-transparent"></div>
            </div>
        </section>
    );
};

export default HeroSection;
